import { createFileRoute, Link, Outlet } from '@tanstack/react-router'
import { SegmentedControl, Tooltip } from '@mantine/core'
import { Globe } from 'lucide-react'
import { AdminScopeProvider, useAdminScope } from './-admin/adminScope'
import '@/styles/pages.css'
import './-admin/admin.css'

export const Route = createFileRoute('/_app/organization/admin')({
  head: () => ({ meta: [{ title: 'Administration · AERRIS' }] }),
  component: () => (
    <AdminScopeProvider>
      <AdministrationShell />
    </AdminScopeProvider>
  ),
})

interface TabDef {
  id: string
  label: string
  to: string
  /** Tabs with no Client-independent form are disabled in global-default mode. */
  clientOnly?: boolean
}

const TABS: TabDef[] = [
  { id: 'taxonomy', label: 'Taxonomy', to: '/organization/admin/taxonomy' },
  { id: 'prompts', label: 'Prompts', to: '/organization/admin/prompts' },
  { id: 'risk', label: 'Risk', to: '/organization/admin/risk' },
  { id: 'access', label: 'Access', to: '/organization/admin/access' },
  { id: 'runs', label: 'Runs', to: '/organization/admin/runs', clientOnly: true },
  { id: 'versions', label: 'Versions', to: '/organization/admin/versions' },
]

function AdministrationShell() {
  const { isGlobalAdmin, globalMode, setGlobalMode, selectedClient, scopeName } = useAdminScope()

  return (
    <div className="admin-shell">
      <aside className="admin-rail" aria-label="Administration sections">
        <div className="admin-rail__header">
          <h1 className="admin-rail__title">Administration</h1>
          <div className="admin-scope">
            <span className="admin-scope__label">Editing</span>
            {isGlobalAdmin ? (
              // Platform admins switch between the selected client and the global default template
              // every client inherits from.
              <SegmentedControl
                className="admin-scope-switch"
                aria-label="Editing"
                fullWidth
                size="xs"
                value={globalMode ? 'global' : 'client'}
                onChange={(v) => setGlobalMode(v === 'global')}
                data={[
                  { value: 'client', label: selectedClient?.name ?? 'Client' },
                  { value: 'global', label: 'Global default' },
                ]}
              />
            ) : (
              <strong className="admin-scope__name">{selectedClient?.name ?? 'No client selected'}</strong>
            )}
            {isGlobalAdmin && !globalMode && !selectedClient && (
              <span className="admin-scope__hint">No client selected</span>
            )}
          </div>
        </div>

        <nav className="admin-rail__nav">
          {TABS.map((tab) => {
            const disabled = tab.clientOnly && globalMode
            if (disabled) {
              return (
                <Tooltip key={tab.id} label="Select a client (not available for the global default)" withArrow>
                  <span className="admin-rail__item admin-rail__item--disabled" aria-disabled="true">{tab.label}</span>
                </Tooltip>
              )
            }
            return (
              <Link
                key={tab.id}
                to={tab.to}
                search={(prev) => prev}
                className="admin-rail__item"
                activeProps={{ className: 'admin-rail__item admin-rail__item--active' }}
                activeOptions={{ exact: false }}
              >
                {tab.label}
              </Link>
            )
          })}
        </nav>
      </aside>

      <div className="admin-outlet" key={globalMode ? 'global' : scopeName}>
        {globalMode && (
          <div className="admin-global-banner" role="note">
            <Globe size={16} aria-hidden />
            <span>
              <strong>You’re editing the global default.</strong> Every client inherits these settings unless
              it has its own override.
            </span>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  )
}
